// src/pages/admin/AdminUnverifiedEmails.js
// File de relance des comptes dont l'email n'est jamais confirme.
//
// Pourquoi cette page existe : un marchand sans email verifie est bloque a la
// publication (`canDealGoLive`). Il ne le voit pas toujours et n'ouvre pas
// forcement son back-office. L'admin doit pouvoir relancer, un par un ou en lot.
//
// « Dernier envoi » vient du serveur (deduit de l'expiration du jeton, 24 h) :
// il evite de renvoyer un email a quelqu'un qui vient d'en recevoir un.
// react-icons, aucun emoji. Maxula

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FiMail,
  FiSearch,
  FiRefreshCw,
  FiCheckSquare,
  FiSquare,
  FiSend,
  FiChevronLeft,
  FiChevronRight,
  FiAlertCircle
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';

const safeDate = (d) => {
  try {
    if (!d) return null;
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : dt;
  } catch {
    return null;
  }
};

const formatDate = (d) => {
  const dt = safeDate(d);
  return dt ? dt.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';
};

const daysSince = (d) => {
  const dt = safeDate(d);
  if (!dt) return null;
  return Math.floor((Date.now() - dt.getTime()) / (24 * 60 * 60 * 1000));
};

const AdminUnverifiedEmails = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('merchants');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);

  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [report, setReport] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, debouncedSearch, scope]);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const params = { page, limit: perPage, scope };
      if (debouncedSearch) params.q = debouncedSearch;
      const res = await API.get('/admin/unverified-emails', { params });
      const payload = res?.data || {};
      if (!mountedRef.current) return;
      setRows(Array.isArray(payload.data) ? payload.data : []);
      setTotal(Number(payload.total) || 0);
      setSelectedIds([]);
    } catch (e) {
      toast.error('Erreur lors du chargement de la liste');
      if (mountedRef.current) {
        setRows([]);
        setTotal(0);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || rows.length) / perPage)),
    [total, rows.length, perPage]
  );

  const pageIds = rows.map((r) => String(r.id));
  const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.includes(id));

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : pageIds);

  const applyResult = (d, label) => {
    if (d.sent > 0) toast.success(d.message || `${d.sent} email(s) envoyé(s)`);
    else toast.warn(d.message || 'Aucun email envoyé');
    setReport({
      label,
      sent: Number(d.sent) || 0,
      skipped: Array.isArray(d.skipped) ? d.skipped : [],
      remaining: Number(d.remaining) || 0
    });
  };

  // scope : 'one' | 'selection' | 'all'
  const resend = async (mode, oneId = null) => {
    if (busy) return;
    let payload;
    if (mode === 'one') payload = { ids: [oneId] };
    else if (mode === 'selection') {
      if (!selectedIds.length) return toast.error('Aucun compte sélectionné');
      payload = { ids: selectedIds };
    } else payload = { all: true, scope };

    if (mode === 'one') setSendingId(oneId);
    setBusy(true);
    try {
      const res = await API.post('/admin/unverified-emails/resend', payload);
      const d = res?.data || {};
      if (mode === 'one') {
        if (d.sent > 0) toast.success('Email de vérification renvoyé');
        else toast.warn(d.skipped?.[0]?.reason || 'Email non envoyé');
      } else {
        applyResult(
          d,
          mode === 'selection'
            ? `${selectedIds.length} compte(s) sélectionné(s)`
            : 'toute la liste'
        );
      }
      setSelectedIds([]);
      setConfirmAll(false);
      await fetchRows();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Erreur lors de l'envoi");
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        setSendingId(null);
      }
    }
  };

  return (
    <div className="p-6">
      {confirmAll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Renvoyer l'email à toute la liste ?
            </h3>
            <p className="text-sm text-gray-600">
              {total} compte(s) concerné(s). Les comptes ayant déjà reçu un email
              il y a moins de 10 minutes sont automatiquement ignorés, pour éviter
              que les envois soient marqués comme indésirables.
            </p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                disabled={busy}
                onClick={() => setConfirmAll(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => resend('all')}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60"
              >
                {busy ? 'Envoi en cours…' : 'Envoyer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {report && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Relance — résultat
            </h3>
            <p className="text-sm text-gray-700">
              <strong>{report.sent}</strong> email(s) de vérification envoyé(s) sur{' '}
              {report.label}.
              {report.remaining > 0 && (
                <>
                  {' '}Il reste <strong>{report.remaining}</strong> compte(s) :
                  relancez l'action pour continuer.
                </>
              )}
            </p>

            {report.skipped.length > 0 && (
              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-900 mb-2">
                  {report.skipped.length} compte(s) ignoré(s) :
                </p>
                <div className="max-h-64 overflow-y-auto border rounded-lg divide-y">
                  {report.skipped.map((it) => (
                    <div key={it.id} className="px-3 py-2 text-sm">
                      <div className="font-medium text-gray-900 truncate">
                        {it.name || `Compte #${it.id}`}
                      </div>
                      <div className="text-xs text-red-700">{it.reason}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end mt-5">
              <button
                type="button"
                onClick={() => setReport(null)}
                className="px-4 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Emails non vérifiés</h1>
        <p className="text-gray-600 mt-1">
          Tant que l'email n'est pas confirmé, les annonces du marchand ne peuvent
          pas être publiées. Relancez-le d'ici.
        </p>

        <div className="bg-white rounded-lg shadow p-4 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2 relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par email ou nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Rechercher"
              />
            </div>

            <select
              value={scope}
              onChange={(e) => {
                setScope(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              aria-label="Périmètre"
            >
              <option value="merchants">Marchands uniquement</option>
              <option value="all">Tous les comptes</option>
            </select>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 border-t pt-4">
            <span className="text-sm text-gray-600 inline-flex items-center gap-2">
              <FiMail className="text-gray-400" />
              À relancer : <strong className="text-gray-900">{total}</strong>
            </span>

            {selectedIds.length > 0 && (
              <>
                <span className="text-sm font-semibold text-primary-900">
                  {selectedIds.length} sélectionné(s)
                </span>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => resend('selection')}
                  className="px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  <FiSend />
                  {busy ? 'Envoi…' : 'Renvoyer à la sélection'}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedIds([])}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm hover:bg-gray-50"
                >
                  Effacer
                </button>
              </>
            )}

            <button
              type="button"
              disabled={busy || !total}
              onClick={() => setConfirmAll(true)}
              className="px-3 py-2 rounded-lg border border-primary-600 text-primary-700 text-sm font-semibold hover:bg-primary-50 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <FiRefreshCw />
              Renvoyer à tous ({total})
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    disabled={!pageIds.length}
                    className="text-gray-500 hover:text-primary-600 disabled:opacity-30"
                    aria-label="Tout sélectionner"
                    title="Tout sélectionner sur cette page"
                  >
                    {allSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compte
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enseigne
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscrit le
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernier envoi
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
                    <p className="mt-2 text-gray-500">Chargement...</p>
                  </td>
                </tr>
              ) : rows.length > 0 ? (
                rows.map((r) => {
                  const age = daysSince(r.createdAt);
                  return (
                    <tr
                      key={r.id}
                      className={
                        selectedIds.includes(String(r.id))
                          ? 'bg-primary-50'
                          : 'hover:bg-gray-50'
                      }
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => toggleSelect(r.id)}
                          className="text-gray-500 hover:text-primary-600"
                          aria-label={`Sélectionner ${r.email}`}
                        >
                          {selectedIds.includes(String(r.id)) ? (
                            <FiCheckSquare size={18} className="text-primary-600" />
                          ) : (
                            <FiSquare size={18} />
                          )}
                        </button>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {`${r.firstName || ''} ${r.lastName || ''}`.trim() || '—'}
                        </div>
                        <div className="text-sm text-gray-500">{r.email}</div>
                        {r.phone && (
                          <a
                            href={`tel:${r.phone}`}
                            className="text-xs text-primary-600 hover:underline"
                          >
                            {r.phone}
                          </a>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {r.merchant?.businessName || '—'}
                        </div>
                        {r.merchant && (
                          <div className="text-xs mt-1">
                            {r.merchant.verified ? (
                              <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                                Compte validé
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800">
                                Compte à valider
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>{formatDate(r.createdAt)}</div>
                        {age !== null && age >= 3 && (
                          <div className="text-xs text-red-600 inline-flex items-center gap-1 mt-1">
                            <FiAlertCircle size={12} /> {age} jours sans confirmation
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {r.neverSent ? (
                          <span className="text-red-600">Jamais envoyé</span>
                        ) : (
                          formatDate(r.lastSentAt)
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => resend('one', r.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
                        >
                          <FiSend
                            size={14}
                            className={sendingId === r.id ? 'animate-pulse' : ''}
                          />
                          Renvoyer
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <FiMail className="mx-auto text-4xl mb-2" />
                    <p>Aucun compte en attente de vérification d'email</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t bg-white">
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <FiChevronLeft /> <span>Préc</span>
            </button>
            <button
              type="button"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages || loading}
              className="px-3 py-2 border rounded disabled:opacity-50 flex items-center space-x-1"
            >
              <span>Suiv</span> <FiChevronRight />
            </button>
            <div className="ml-4 text-sm text-gray-600">
              Page <strong>{page}</strong> / <strong>{totalPages}</strong>
            </div>
            <div className="ml-4 text-sm text-gray-600">
              <span className="mr-2">Afficher</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="border px-2 py-1 rounded"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <span className="ml-2">/ page</span>
            </div>
          </div>

          <div className="text-sm text-gray-600">Total : {total}</div>
        </div>
      </div>
    </div>
  );
};

export default AdminUnverifiedEmails;
